import React from 'react';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import ChatSidebar from '../chat/ChatSidebar';
import SettingsOverlay from '../settings/SettingsOverlay';
import DualChatLayout from '../DualChatLayout';
import ChatWindow from '../chat/ChatWindow';
import ArtifactRenderer from '../ArtifactRenderer';
import MessageInput from '../input/MessageInput';
import Toolbar from '../toolbar/Toolbar';
import WorkspaceHome from '../projects/WorkspaceHome';
import useDynamicGreeting from '../../hooks/ui/useDynamicGreeting';

const getDesktopAppScale = () => {
  const scaleValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--arrow-desktop-app-scale')
    .trim();
  const scale = Number(scaleValue);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

const DesktopResizeHandle = ({ chatWidth, artifactWidth, isSidebarOpen, onResize }) => (
  <div
    className="absolute left-0 top-0 bottom-0 w-1 hover:w-2 cursor-col-resize transition-all duration-150 z-10 group"
    onMouseDown={(e) => {
      e.preventDefault();
      const appScale = getDesktopAppScale();
      const startX = e.clientX / appScale;
      const startChatWidth = chatWidth;
      const containerWidth = (window.innerWidth / appScale) - (isSidebarOpen ? 320 : 0);
      
      // Get references to the elements we need to resize
      const layoutRoot = e.currentTarget.closest('.arrow-desktop-layout-root');
      const chatElement = layoutRoot?.querySelector('[data-chat-section]');
      const artifactElement = e.target.closest('[data-artifact-section]');
      const toolbarElement = document.querySelector('[data-toolbar]');
      
      // Disable transitions during drag for smooth performance
      if (chatElement) chatElement.style.transition = 'none';
      if (artifactElement) artifactElement.style.transition = 'none';
      if (toolbarElement) toolbarElement.style.transition = 'none';
      
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 16; // ~60fps

      const handleMouseMove = (e) => {
        const now = Date.now();
        if (now - lastUpdateTime < UPDATE_INTERVAL) return;
        lastUpdateTime = now;
        
        const deltaX = (e.clientX / appScale) - startX;
        const deltaPercent = (deltaX / containerWidth) * 100;
        
        const newChatWidth = Math.max(30, Math.min(80, startChatWidth + deltaPercent));
        const newArtifactWidth = 100 - newChatWidth;
        
        // Update DOM directly for smooth performance
        if (chatElement) {
          chatElement.style.width = `${newChatWidth}%`;
        }
        if (artifactElement) {
          artifactElement.style.width = `${newArtifactWidth}%`;
        }
        if (toolbarElement) {
          toolbarElement.style.right = `${newArtifactWidth}%`;
        }
      };

      const handleMouseUp = (e) => {
        // Calculate final dimensions
        const deltaX = (e.clientX / appScale) - startX;
        const deltaPercent = (deltaX / containerWidth) * 100;
        const finalChatWidth = Math.max(30, Math.min(80, startChatWidth + deltaPercent));
        const finalArtifactWidth = 100 - finalChatWidth;
        
        // Re-enable transitions
        if (chatElement) chatElement.style.transition = '';
        if (artifactElement) artifactElement.style.transition = '';
        if (toolbarElement) toolbarElement.style.transition = '';
        
        // Update React state once at the end
        onResize(finalChatWidth, finalArtifactWidth);
        
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }}
  >
    {/* Resize handle indicator */}
    <div className="absolute left-1/4 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-8 bg-gray-400 dark:bg-gray-500 rounded-full opacity-60 group-hover:opacity-100 group-hover:scale-125 transition-all duration-150"></div>
  </div>
);

const DesktopLayout = ({
  // Chat state
  selectedChat,
  messages,
  secondaryMessages,
  selectedModel,
  selectedModelName,
  secondaryModel,
  isDualMode,
  setIsDualMode,
  setSecondaryModel,
  reasoningStream,
  inputText,
  showSettings,
  chatTitle,
  messageLimit,
  isLoadingMessages,
  editingMessage,
  
  // Subscription state
  hasSubscription,
  hasBasicSubscription,
  subscriptionSources,
  hasMaxSubscription,
  subscriptionStatus,
  isCheckingSubscription,
  onRefreshSubscription,
  
  // UI state
  isSidebarOpen,
  chatWidth,
  artifactWidth,
  currentArtifact,
  isArtifactExpanded,
  particlesEnabled,
  setParticlesEnabled,
  chatSidebarViewMode,
  setChatSidebarViewMode,
  
  // User data
  userName,
  userEmail,
  chats,
  projects,
  activeProject,
  activeProjectId,
  isLoadingProjects,
  projectError,
  
  // Handlers
  toggleSidebar,
  onNewChat,
  onChatSelect,
  onProjectSelect,
  onCreateProject,
  onUpdateProject,
  onDeleteProject,
  onMoveChatToProject,
  onUpgrade,
  onSettingsClick,
  onBackToChat,
  onLogout,
  onQuestionClick,
  onArtifactOpen,
  onArtifactClose,
  onToggleArtifactExpanded,
  onResize,
  onModelChange,
  onPinnedModelChange,
  onMessageSent,
  onSecondaryMessageSent,
  updateChatTitle,
  onReasoningStreamUpdate,
  setInputText,
  onStartEditMessage,
  onCancelEdit,
  applyEditAndTruncate,
  realtimeVoice
}) => {
  const { greeting } = useDynamicGreeting(selectedChat?.id);
  const currentMessages = messages[selectedChat?.id] || [];
  // Treat existing-chat loading differently so we can show a loading state instead of the new chat empty screen.
  const isLoadingExistingChat = Boolean(selectedChat?.id && isLoadingMessages);
  const isEmptyChat = currentMessages.length === 0 && !isLoadingExistingChat;
  const activeProjectChats = React.useMemo(() => {
    if (!activeProjectId) return [];
    return chats.filter((chat) => !chat.archived && (chat.projectId || chat.project_id) === activeProjectId);
  }, [activeProjectId, chats]);
  const workspaceComposerPlaceholder = activeProject?.name
    ? `New chat in ${activeProject.name}`
    : 'Ask anything';

  const renderNewChatComposer = (placeholder = 'Ask anything') => (
    <motion.div
      layoutId="message-input-shell"
      className="w-full max-w-4xl"
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
    >
      <MessageInput
        selectedChat={selectedChat}
        activeProjectId={activeProjectId}
        projects={projects}
        onProjectSelect={onProjectSelect}
        selectedModel={selectedModel}
        selectedModelName={selectedModelName}
        onModelChange={onModelChange}
        onPinnedModelChange={onPinnedModelChange}
        onMessageSent={onMessageSent}
        messages={[]}
        updateChatTitle={updateChatTitle}
        hasSubscription={hasSubscription}
        hasBasicSubscription={hasBasicSubscription}
        onUpgrade={onUpgrade}
        messageLimit={messageLimit}
        disableInput={false}
        onReasoningStreamUpdate={onReasoningStreamUpdate}
        inputText={inputText}
        setInputText={setInputText}
        isDualMode={isDualMode}
        secondaryModel={secondaryModel}
        onSecondaryMessageSent={onSecondaryMessageSent}
        secondaryMessages={[]}
        editingMessage={editingMessage}
        onCancelEdit={onCancelEdit}
        applyEditAndTruncate={applyEditAndTruncate}
        realtimeVoice={realtimeVoice}
        placeholder={placeholder}
      />
    </motion.div>
  );

  return (
    <div className="arrow-desktop-layout-root flex h-full w-full absolute z-10 transition-colors duration-200">
      <div
        className={`
          flex-shrink-0
          transition-all
          duration-300
          ease-in-out
          ${isSidebarOpen ? 'w-[320px]' : 'w-[56px]'}
        `}
      >
        <ChatSidebar
          isOpen={isSidebarOpen}
          chats={chats}
          projects={projects}
          activeProjectId={activeProjectId}
          isLoadingProjects={isLoadingProjects}
          projectError={projectError}
          onChatSelect={onChatSelect}
          onProjectSelect={onProjectSelect}
          onCreateProject={onCreateProject}
          onUpdateProject={onUpdateProject}
          onDeleteProject={onDeleteProject}
          onMoveChatToProject={onMoveChatToProject}
          onNewChat={onNewChat}
          toggleSidebar={toggleSidebar}
          selectedChatId={selectedChat?.id}
          hasSubscription={hasSubscription}
          hasBasicSubscription={hasBasicSubscription}
          onUpgrade={onUpgrade}
          userName={userName}
          userEmail={userEmail}
          onSettingsClick={onSettingsClick}
          isMobile={false}
          viewMode={chatSidebarViewMode}
          messagesByChatId={messages}
        />
      </div>
      
      <div 
        className={`relative flex flex-col min-w-0 transition-all duration-300`}
        style={{ width: currentArtifact && !isArtifactExpanded ? `${chatWidth}%` : '100%' }}
        data-chat-section
      >
        <Toolbar
          toggleSidebar={toggleSidebar}
          onNewChat={onNewChat}
          chatTitle={chatTitle}
        model={selectedChat ? selectedChat.model : selectedModel}
        isSidebarOpen={isSidebarOpen}
        selectedModel={selectedModel}
        sidebarWidth={isSidebarOpen ? 300 : 0}
        onUpgrade={onUpgrade}
        particlesEnabled={particlesEnabled}
        setParticlesEnabled={setParticlesEnabled}
          chatSidebarViewMode={chatSidebarViewMode}
          setChatSidebarViewMode={setChatSidebarViewMode}
          hasArtifact={!!currentArtifact}
        isArtifactExpanded={isArtifactExpanded}
        chatWidth={chatWidth}
        isDualMode={isDualMode}
      />

        <div className="flex flex-grow overflow-hidden pt-16">
          {hasSubscription ? (
            <div className="flex flex-col w-full min-w-0">
                <LayoutGroup id="desktop-chat-flow">
                  <AnimatePresence mode="popLayout">
                    {(isEmptyChat || isLoadingExistingChat) && !isDualMode ? (
                      <motion.div
                        key={isLoadingExistingChat ? "chat-loading" : activeProject ? `workspace-home-${activeProject.id}` : "empty-chat"}
                        className={
                          isLoadingExistingChat || !activeProject
                            ? "flex-grow flex flex-col items-center pt-[21vh] px-4"
                            : "flex-grow overflow-y-auto px-4 pb-12"
                        }
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -14, scale: 0.98 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                      >
                        {isLoadingExistingChat ? (
                          <div className="flex flex-col items-center space-y-3 text-center">
                            <div className="flex items-center space-x-3">
                              <div className="h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-indigo-500 dark:border-t-indigo-400 rounded-full animate-spin" aria-hidden="true" />
                              <p className="text-lg font-medium text-gray-800 dark:text-gray-100">Loading messages…</p>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Fetching your previous conversation.</p>
                          </div>
                        ) : activeProject ? (
                          <WorkspaceHome
                            project={activeProject}
                            chats={activeProjectChats}
                            onChatSelect={onChatSelect}
                            renderComposer={() => renderNewChatComposer(workspaceComposerPlaceholder)}
                          />
                        ) : (
                          <>
                            <h1 className="text-3xl font-semibold text-black dark:text-white mb-8">
                              {greeting}
                            </h1>
                            {renderNewChatComposer()}
                          </>
                        )}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="chat-layout"
                        className="flex flex-col flex-grow min-h-0"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                      >
                        <motion.div
                          layout
                          className="arrow-chat-scroll-frame flex-grow overflow-y-auto"
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          <div className="w-full mx-auto h-full">
                            {isDualMode ? (
                              <DualChatLayout
                                selectedChat={selectedChat}
                                primaryMessages={currentMessages}
                                secondaryMessages={secondaryMessages[selectedChat?.id] || []}
                                primaryModel={selectedModel}
                                secondaryModel={secondaryModel || selectedModel}
                                reasoningStream={reasoningStream}
                                onQuestionClick={onQuestionClick}
                                onArtifactOpen={onArtifactOpen}
                                onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                                onMessageSent={onMessageSent}
                                updateChatTitle={updateChatTitle}
                                hasSubscription={hasSubscription}
                                messageLimit={messageLimit}
                                editingMessage={editingMessage}
                                onCancelEdit={onCancelEdit}
                                applyEditAndTruncate={applyEditAndTruncate}
                              />
                            ) : (
                              <ChatWindow
                                selectedChat={selectedChat}
                                messages={currentMessages}
                                selectedModel={selectedModel}
                                isLoadingMessages={isLoadingMessages}
                                reasoningStream={reasoningStream}
                                onQuestionClick={onQuestionClick}
                                onArtifactOpen={onArtifactOpen}
                                onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                                onMessageSent={onMessageSent}
                                updateChatTitle={updateChatTitle}
                                hasSubscription={hasSubscription}
                                messageLimit={messageLimit}
                                editingMessage={editingMessage}
                                onCancelEdit={onCancelEdit}
                                applyEditAndTruncate={applyEditAndTruncate}
                              />
                            )}
                          </div>
                        </motion.div>
                        <motion.div
                          layoutId="message-input-shell"
                          className="flex-shrink-0 flex justify-center"
                          transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                        >
                          <MessageInput
                            selectedChat={selectedChat}
                            activeProjectId={activeProjectId}
                            projects={projects}
                            onProjectSelect={onProjectSelect}
                            selectedModel={selectedModel}
                            selectedModelName={
                              selectedChat ? selectedChat.model : selectedModelName
                            }
                            onModelChange={onModelChange}
                            onPinnedModelChange={onPinnedModelChange}
                            onMessageSent={onMessageSent}
                            messages={currentMessages}
                            updateChatTitle={updateChatTitle}
                            hasSubscription={hasSubscription}
                            hasBasicSubscription={hasBasicSubscription}
                            onUpgrade={onUpgrade}
                            messageLimit={messageLimit}
                            disableInput={
                              (selectedChat?.id && currentMessages.length / 2 >= 30) ||
                              selectedModel === 'DeprecatedModel'
                            }
                            onReasoningStreamUpdate={onReasoningStreamUpdate}
                            inputText={inputText}
                            setInputText={setInputText}
                            isDualMode={isDualMode}
                            secondaryModel={secondaryModel}
                            onSecondaryMessageSent={onSecondaryMessageSent}
                            secondaryMessages={secondaryMessages[selectedChat?.id] || []}
                            editingMessage={editingMessage}
                            onCancelEdit={onCancelEdit}
                            applyEditAndTruncate={applyEditAndTruncate}
                            realtimeVoice={realtimeVoice}
                          />
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </LayoutGroup>
            </div>
          ) : (
            <>
              <div className="flex flex-col w-full min-w-0">
                  <LayoutGroup id="desktop-chat-flow-guest">
                    <AnimatePresence mode="popLayout">
                      {(isEmptyChat || isLoadingExistingChat) && !isDualMode ? (
                        <motion.div
                          key={isLoadingExistingChat ? "chat-loading-guest" : activeProject ? `workspace-home-guest-${activeProject.id}` : "empty-chat-guest"}
                          className={
                            isLoadingExistingChat || !activeProject
                              ? "flex-grow flex flex-col items-center pt-[21vh] px-4"
                              : "flex-grow overflow-y-auto px-4 pb-12"
                          }
                          initial={{ opacity: 0, y: 16, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -14, scale: 0.98 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          {isLoadingExistingChat ? (
                            <div className="flex flex-col items-center space-y-3 text-center">
                              <div className="flex items-center space-x-3">
                                <div className="h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-indigo-500 dark:border-t-indigo-400 rounded-full animate-spin" aria-hidden="true" />
                                <p className="text-lg font-medium text-gray-800 dark:text-gray-100">Loading messages…</p>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Fetching your previous conversation.</p>
                            </div>
                          ) : activeProject ? (
                            <WorkspaceHome
                              project={activeProject}
                              chats={activeProjectChats}
                              onChatSelect={onChatSelect}
                              renderComposer={() => renderNewChatComposer(workspaceComposerPlaceholder)}
                            />
                          ) : (
                            <>
                              <h1 className="text-3xl font-semibold text-black dark:text-white mb-8">
                                {greeting}
                              </h1>
                              {renderNewChatComposer()}
                            </>
                          )}
                        </motion.div>
                      ) : (
                        <motion.div
                          key="chat-layout-guest"
                          className="flex flex-col flex-grow min-h-0"
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 12 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          <motion.div
                            layout
                            className="flex-grow overflow-y-auto"
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                          >
                            {isDualMode ? (
                              <DualChatLayout
                                selectedChat={selectedChat}
                                primaryMessages={currentMessages}
                                secondaryMessages={secondaryMessages[selectedChat?.id] || []}
                                primaryModel={selectedModel}
                                secondaryModel={secondaryModel || selectedModel}
                                reasoningStream={reasoningStream}
                                onQuestionClick={onQuestionClick}
                                onArtifactOpen={onArtifactOpen}
                                onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                                onMessageSent={onMessageSent}
                                updateChatTitle={updateChatTitle}
                                hasSubscription={hasSubscription}
                                messageLimit={messageLimit}
                                editingMessage={editingMessage}
                                onCancelEdit={onCancelEdit}
                                applyEditAndTruncate={applyEditAndTruncate}
                              />
                            ) : (
                              <ChatWindow
                                selectedChat={selectedChat}
                                messages={currentMessages}
                                selectedModel={selectedModel}
                                isLoadingMessages={isLoadingMessages}
                                reasoningStream={reasoningStream}
                                onQuestionClick={onQuestionClick}
                                onArtifactOpen={onArtifactOpen}
                                onEditMessage={onStartEditMessage ? (msg) => onStartEditMessage(selectedChat?.id, msg) : undefined}
                                onMessageSent={onMessageSent}
                                updateChatTitle={updateChatTitle}
                                hasSubscription={hasSubscription}
                                messageLimit={messageLimit}
                                editingMessage={editingMessage}
                                onCancelEdit={onCancelEdit}
                                applyEditAndTruncate={applyEditAndTruncate}
                              />
                            )}
                          </motion.div>
                          <motion.div
                            layoutId="message-input-shell"
                            className="flex-shrink-0 flex justify-center"
                            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                          >
                            <div className="w-[80%]">
                              <MessageInput
                                selectedChat={selectedChat}
                                activeProjectId={activeProjectId}
                                projects={projects}
                                onProjectSelect={onProjectSelect}
                                selectedModel={selectedModel}
                                selectedModelName={
                                  selectedChat ? selectedChat.model : selectedModelName
                                }
                                onModelChange={onModelChange}
                                onPinnedModelChange={onPinnedModelChange}
                                onMessageSent={onMessageSent}
                                messages={currentMessages}
                                updateChatTitle={updateChatTitle}
                                hasSubscription={hasSubscription}
                                hasBasicSubscription={hasBasicSubscription}
                                onUpgrade={onUpgrade}
                                messageLimit={messageLimit}
                                disableInput={
                                  (selectedChat?.id && currentMessages.length / 2 >= 30) ||
                                  selectedModel === 'DeprecatedModel'
                                }
                              onReasoningStreamUpdate={onReasoningStreamUpdate}
                              inputText={inputText}
                              setInputText={setInputText}
                              isDualMode={isDualMode}
                              secondaryModel={secondaryModel}
                              onSecondaryMessageSent={onSecondaryMessageSent}
                              secondaryMessages={secondaryMessages[selectedChat?.id] || []}
                              editingMessage={editingMessage}
                                onCancelEdit={onCancelEdit}
                                applyEditAndTruncate={applyEditAndTruncate}
                                realtimeVoice={realtimeVoice}
                              />
                            </div>
                          </motion.div>
                      </motion.div>
                    )}
                    </AnimatePresence>
                  </LayoutGroup>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right-side artifact panel - full height */}
      {currentArtifact && !isArtifactExpanded && (
        <div 
          className="flex-shrink-0 h-full transition-all duration-300 relative"
          style={{ width: `${artifactWidth}%` }}
          data-artifact-section
        >
          <DesktopResizeHandle
            chatWidth={chatWidth}
            artifactWidth={artifactWidth}
            isSidebarOpen={isSidebarOpen}
            onResize={onResize}
          />
          <ArtifactRenderer
            artifact={currentArtifact}
            onClose={onArtifactClose}
            isExpanded={isArtifactExpanded}
            onToggleExpanded={onToggleArtifactExpanded}
          />
        </div>
      )}

      {/* Full-width artifact overlay when expanded */}
      {currentArtifact && isArtifactExpanded && (
        <div className="absolute inset-0 z-20 bg-white dark:bg-gray-900">
          <ArtifactRenderer
            artifact={currentArtifact}
            onClose={onArtifactClose}
            isExpanded={isArtifactExpanded}
            onToggleExpanded={onToggleArtifactExpanded}
          />
        </div>
      )}

      <SettingsOverlay
        isOpen={showSettings}
        onClose={onBackToChat}
        hasSubscription={hasSubscription}
        hasBasicSubscription={hasBasicSubscription}
        subscriptionSources={subscriptionSources}
          hasMaxSubscription={hasMaxSubscription}
          subscriptionStatus={subscriptionStatus}
          isCheckingSubscription={isCheckingSubscription}
          onRefreshSubscription={onRefreshSubscription}
        onLogout={onLogout}
        onUpgrade={onUpgrade}
        particlesEnabled={particlesEnabled}
        setParticlesEnabled={setParticlesEnabled}
        chatSidebarViewMode={chatSidebarViewMode}
        setChatSidebarViewMode={setChatSidebarViewMode}
      />
    </div>
  );
};

export default DesktopLayout;
